import { commonTexts, settlementTexts } from 'consts/text';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import {
    AdditionalLessonDTO,
    CourseId,
    PaymentConfiguration,
    SettlementConfiguration,
} from 'types';
import { additionalLessonDTOFields } from 'consts/additionalLessons/additionalLessonDTOFields';
import { AppTable, AppTableProps, RemoveModal } from 'components/organisms';
import { AppButton, Option } from 'components/atoms';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import styled, { css } from 'styled-components';
import { useMemo, useState, useEffect } from 'react';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { parsePaginationDataToTablePaginationData } from 'utils/parsers/table/parsePaginationDataToTablePaginationData';

import { AdditionalLessonJournalSelectModal } from 'components/templates/settlement/AdditionalLessonJournal/AdditionalLessonJournalSelectModal';
import {
    AdditionalLessonsPaymentTypeColumn,
    AdditionalLessonsPriceColumn,
    AdditionalLessonsSettlementTypeColumn,
} from 'components/atoms/AdditionalLessonsColumns';
import { noAdditionalLessonConfigurationColor } from 'consts/additionalLessons/noAdditionalLessonConfigurationColor';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { CourseFullDTO } from 'types/course/course';
import { AddCourseModal } from 'components/templates/settlement/AddCourseModal/AddCourseModal';
import { EditCourseModal } from 'components/templates/settlement/AddCourseModal/EditCourseModal';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';

const AdditionalLessonDisplay = styled(AppButton)<{ color?: string }>`
    ${({ theme, color }) => css`
        margin: 0;
        padding: 0;
        font-weight: normal;
        color: ${color ?? theme.colors.primary} !important;
    `}
`;

const columns = (
    setSelectedLesson: (record: AdditionalLessonDTO) => void,
): AppTableProps<AdditionalLessonDTO>['columns'] => [
    {
        title: settlementTexts.extraLessons.columnLabels.name,
        dataIndex: additionalLessonDTOFields.courseName,
        key: additionalLessonDTOFields.courseName,
        render: (_, record) => (
            <AdditionalLessonDisplay
                type="link"
                color={!record.configured ? noAdditionalLessonConfigurationColor : 'primary'}
                onClick={() => setSelectedLesson(record)}
            >
                {record.courseName}
            </AdditionalLessonDisplay>
        ),
    },
    {
        title: settlementTexts.extraLessons.columnLabels.price,
        dataIndex: additionalLessonDTOFields.price,
        key: additionalLessonDTOFields.price,
        render: (_, record) => <AdditionalLessonsPriceColumn additionalLesson={record} />,
        isVisibleAsExtendableInMobile: true,
        isInvisibleInMobile: false,
    },
    {
        title: settlementTexts.extraLessons.columnLabels.paymentConfig,
        dataIndex: additionalLessonDTOFields.paymentType,
        key: additionalLessonDTOFields.paymentType,
        render: (_, record) => <AdditionalLessonsPaymentTypeColumn additionalLesson={record} />,
        isVisibleAsExtendableInMobile: true,
        isInvisibleInMobile: false,
    },
    {
        title: settlementTexts.extraLessons.columnLabels.settlementConfig,
        dataIndex: additionalLessonDTOFields.settlementType,
        key: additionalLessonDTOFields.settlementType,
        render: (_, record) => <AdditionalLessonsSettlementTypeColumn additionalLesson={record} />,
        isVisibleAsExtendableInMobile: true,
        isInvisibleInMobile: false,
    },
];

export const AdditionalLessonsPage = () => {
    const [selectedLesson, setSelectedLesson] = useState<AdditionalLessonDTO | null>(null);
    const [selectedLessonIdForJournalSelect, setSelectedLessonIdForJournalSelect] =
        useState<CourseId | null>(null);
    const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
    const [editCourse, setEditCourse] = useState<CourseFullDTO | null>(null);
    const [selectedToRemoveLessonId, setSelectedToRemoveLessonId] = useState<CourseId | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { displayError } = useSimpleNotification();

    const { id: institutionId } = useCurrentInstitution();

    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    const {
        data: coursesData,
        isInitialLoading: isCoursesInitialLoading,
        isRefetching: isCoursesRefetching,
        isError: isCoursesError,
        refetch: refetchCourses,
    } = useAppQuery('COURSES', [institutionId, currentPage, currentPageSize], () =>
        StaffHTTPService.courses.getCourses(institutionId, {
            size: Number(currentPageSize),
            page: Number(currentPage),
        }),
    );

    useEffect(() => {
        if (selectedLesson) {
            StaffHTTPService.courses.getCourse(selectedLesson.id, institutionId).then((res) => {
                setEditCourse(res.data);
            });
        } else {
            setEditCourse(null);
        }
    }, [selectedLesson, institutionId]);

    const onDeselectAdditionalLesson = () => {
        setSelectedLesson(null);
    };

    const onDeselectAdditionalLessonForJournal = () => {
        setSelectedLessonIdForJournalSelect(null);
    };

    const handleRemoveLesson = (additionalLesson: AdditionalLessonDTO) => {
        setSelectedToRemoveLessonId(additionalLesson.id);
    };

    const onLessonRemoveRequestFinishOrCancel = () => setSelectedToRemoveLessonId(null);

    const handleRemoveLessonConfirm = async () => {
        if (!selectedToRemoveLessonId) {
            return;
        }

        setIsDeleting(true);
        try {
            await StaffHTTPService.courses.deleteCourse(selectedToRemoveLessonId, institutionId);
            refetchCourses();
        } catch (error) {
            displayError(
                'Nie udało się usunąć zajęć dodatkowych, ponieważ są one używane w planie zajęć',
            );
        } finally {
            onLessonRemoveRequestFinishOrCancel();
            setIsDeleting(false);
        }
    };

    const options: Option<AdditionalLessonDTO>[] = [
        {
            label: commonTexts.actionLabels.edit,
            onClick: (additionalLesson: AdditionalLessonDTO) => setSelectedLesson(additionalLesson),
            Icon: <IconEdit />,
        },
        {
            label: commonTexts.actionLabels.delete,
            onClick: handleRemoveLesson,
            Icon: <IconTrash />,
        },
    ];

    // Map CourseFullDTO to AdditionalLessonDTO shape for table and modal
    const mappedLessons: AdditionalLessonDTO[] =
        coursesData?.content.map((course: CourseFullDTO) => {
            const config = course.courseSettlementConfigDto;
            const paymentType = (() => {
                switch (config?.calculationType) {
                    case 'FLAT_MONTHLY':
                        return PaymentConfiguration.MONTHLY;
                    case 'PER_UNIT':
                        return PaymentConfiguration.EACH_LESSON;
                    default:
                        return null;
                }
            })();

            const settlementType = (() => {
                switch (config?.paymentType) {
                    case 'PRE_PAID':
                        return SettlementConfiguration.PREPAID;
                    case 'POST_PAID':
                        return SettlementConfiguration.POSTPAID;
                    default:
                        return null;
                }
            })();

            return {
                id: course.courseDto.id,
                courseName: course.courseDto.name,
                price: config?.pricePerUnit ?? config?.priceForMonth ?? null,
                paymentType,
                settlementType,
                configured: !!config,
                free: false, // Set as needed if you have a way to determine this
                vatRate: course.courseTaxDto?.rateType ?? null,
                vatReleaseReasonType: course.courseTaxDto?.vatExemptionReason ?? null,
                customVatReleaseReason: course.courseTaxDto?.customVatReleaseReason ?? null,
            };
        }) ?? [];

    const memorizedColumns = useMemo(() => columns(setSelectedLesson), [setSelectedLesson]);

    return (
        <>
            <AddCourseModal
                open={isAddCourseModalOpen}
                onClose={() => setIsAddCourseModalOpen(false)}
                onSuccess={refetchCourses}
            />
            {selectedLessonIdForJournalSelect && (
                <AdditionalLessonJournalSelectModal
                    onClose={onDeselectAdditionalLessonForJournal}
                    courseId={selectedLessonIdForJournalSelect}
                />
            )}
            {selectedLesson && editCourse && (
                <EditCourseModal
                    open={!!selectedLesson}
                    onClose={onDeselectAdditionalLesson}
                    onSuccess={refetchCourses}
                    course={{
                        ...editCourse.courseSettlementConfigDto,
                        id: editCourse.courseDto.id,
                        name: editCourse.courseDto.name,
                        vatRate: editCourse.courseTaxDto?.rateType,
                        vatExemptionReason: editCourse.courseTaxDto?.vatExemptionReason,
                    }}
                />
            )}
            <RemoveModal
                open={!!selectedToRemoveLessonId}
                title="Czy na pewno chcesz usunąć zajęcia dodatkowe?"
                confirmActionLoading={isDeleting}
                onOk={handleRemoveLessonConfirm}
                onCancel={onLessonRemoveRequestFinishOrCancel}
            />
            <AppTable<AdditionalLessonDTO>
                isError={isCoursesError}
                title={settlementTexts.extraLessons.tableTitle}
                emptyInfo={settlementTexts.extraLessons.emptyInfo}
                columns={memorizedColumns}
                rowKey={({ id }) => id}
                isDataInitialLoading={isCoursesInitialLoading}
                isDataRefetching={isCoursesRefetching}
                options={options}
                dataSource={mappedLessons}
                tableManageUtilsProps={{ isVisibleSearchFilter: false }}
                pagination={{
                    ...parsePaginationDataToTablePaginationData({
                        currentPage,
                        currentPageSize,
                    }),
                    total: coursesData?.totalElements || 0,
                    onChange: handlePaginationValuesChange,
                }}
                addButtonLabel={commonTexts.actionLabels.add}
                onAddButtonClick={() => setIsAddCourseModalOpen(true)}
            />
        </>
    );
};
